/**
 * All scoring rules and constants live here (DECISIONS 2026-09-29, "Transparent points").
 * Small whole numbers a player can add up in their head. Every day is the same three rounds (DECISIONS 2026-09-30):
 *   Pin it and Next stop — points by distance band (20 within 10 km … 1 within 3,000 km).
 *   Pin it hints — each hint moves the most you can score down the ladder: 20, then 15, then 10 (DECISIONS 2026-10-03).
 *   Whose steps? — 20 / 15 / 10 / 7 / 5 by the place it was named at (each place shown or wrong guess is one step down).
 * One rule for all three: distance shows how close you were; every extra step or hint you need moves you down the ladder.
 * So every day is out of 60.
 * Changing any of them changes shared results: log the change in docs/DECISIONS.md first.
 */
import type { Level } from "../puzzles/types";
import type { Playable } from "./derive";

export const ROUNDS_PER_DAY = 3;

/** Pin it and Next stop: [within km, points], checked in order. */
export const DISTANCE_POINTS: readonly (readonly [number, number])[] = [
  [10, 20], [20, 18], [30, 17], [40, 16], [50, 15], [60, 14], [70, 13], [80, 12], [90, 11], [100, 10],
  [200, 8], [300, 7], [400, 6], [500, 5], [1000, 3], [2000, 2], [3000, 1],
];
export const PIN_MAX = DISTANCE_POINTS[0]![1];
/** Rings drawn around the answer after a Pin it round: the distances the How to play text names. */
export const PIN_RINGS_KM = [10, 100, 500] as const;

/** Pin it: the most a round can score after 0, 1 or 2 hints (one step down the ladder per hint). */
export const HINT_CAPS = [20, 15, 10] as const;
export const MAX_HINTS = HINT_CAPS.length - 1;
/** The most a Pin it round can still score after `used` hints. */
export const hintCap = (used: number): number => HINT_CAPS[Math.max(0, Math.min(MAX_HINTS, Math.floor(used)))]!;
/** Pin it points: the distance points, but never more than the hint cap. */
export const pinPoints = (km: number, hintsUsed: number): number => Math.min(distanceScore(km), hintCap(hintsUsed));

/** "Whose steps?": points by the number of places revealed when the answer is found (1st..5th). */
export const WHO_POINTS = [20, 15, 10, 7, 5] as const;
export const WHO_CLUES = WHO_POINTS.length;
export const WHO_MAX = WHO_POINTS[0];

/** Largest total a challenge link may carry (sanity bound for untrusted links and events). */
export const MAX_SCORE_BOUND = 200;

/** Colour bands (verdicts, progress bar) per 1,000 of a round's maximum. */
export const BAND_THRESHOLDS = [300, 600, 900] as const;

/** Kept for map framing of the pin round (how wide "near" is); no longer used for points. */
export const PIN_SCALE_FACTOR: Record<Level, number> = { easy: 2.5, medium: 1, hard: 0.6 };
/** Next stop: how wide the map frames the answer after the round when the round doesn't say. */
export const NEXT_DEFAULT_SCALE_KM = 600;
export function pinScaleKm(scaleKm: number, level: Level): number {
  return scaleKm * PIN_SCALE_FACTOR[level];
}

export const clampRound = (score: number, max: number): number => Math.max(0, Math.min(max, Math.round(score)));

/** A score on the 0–1,000 scale, whatever the round's maximum (for bands and verdict words). */
export const per1000 = (score: number, max: number): number => (max <= 0 ? 0 : (score * 1000) / max);

/** Distance scoring used by Pin it and Next stop. */
export function distanceScore(km: number): number {
  for (const [within, pts] of DISTANCE_POINTS) if (km <= within) return pts;
  return 0;
}

/** `found` = 1-based number of places shown when the right answer was given; null = not found. */
export function whoScore(found: number | null): number {
  if (found == null || found < 1 || found > WHO_CLUES) return 0;
  return WHO_POINTS[found - 1] ?? 0;
}

/** Most points a playable round can give. */
export const maxOf = (p: Pick<Playable, "type">): number => (p.type === "who" ? WHO_MAX : PIN_MAX);

/** Most points a day can give: 20 + 20 + 20. */
export const DAY_MAX = PIN_MAX + PIN_MAX + WHO_MAX;

/** Results saved before 2026-09-29 carry no `max` and used the old 1,000/3,000 scale. */
export const legacyMax = (type: string): number => (type === "who" ? 3000 : 1000);

export function band(raw: number, max: number): 0 | 1 | 2 | 3 {
  const [b1, b2, b3] = BAND_THRESHOLDS;
  const score = per1000(raw, max);
  return score >= b3 ? 3 : score >= b2 ? 2 : score >= b1 ? 1 : 0;
}

/**
 * Verdict colour for a finished round. "Whose steps?" is won or lost: naming the person is green
 * at any place (Egor, 29 Sep), not named is red. Other rounds use the share of their maximum.
 */
export const roundBand = (type: string, raw: number, max: number): 0 | 1 | 2 | 3 => (type === "who" ? (raw > 0 ? 3 : 0) : band(raw, max));

/** "Whose steps?": the place (1–5) a score was earned at, or 0 if not named. */
export const whoPlaceOf = (raw: number): number => WHO_POINTS.indexOf(raw as (typeof WHO_POINTS)[number]) + 1;
